import axios, { type AxiosInstance } from "axios"
import { getExhibitionHeader } from "../utils/exhibitionContext";
import type {
  TicketPayment,
  Ticket,
  
  PaginatedResponse,
} from "../Component/Application/TicketPayment/types"
import type { GateAssignment, AccountPerson } from "../Component/Application/GateAssignment/types"
import type { VehicleVisit,  } from "../Component/Application/VehicleVisit/types"
const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
})

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken")
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`
    
    Object.assign(config.headers, getExhibitionHeader());}
    return config
  },
  (error) => Promise.reject(error),
)

// Ticket Payment Service
export const TicketPaymentService = {
  getAll: async (page = 1, pageSize = 10, search = ""): Promise<PaginatedResponse<TicketPayment>> => {
    const response = await api.get("/api/entries/ticket-payments/", {
      params: { page, page_size: pageSize, search },
    })
    return response.data
  },

  getById: async (id: number): Promise<{ payment: TicketPayment; tickets: Ticket[] }> => {
    const response = await api.get(`/api/entries/ticket-payments/${id}/`)
    return response.data
  },

  create: async (data: {
    tx_reference: string
    operator: string
    phone_number: string
    amount: number
    num_tickets: number
  }): Promise<any> => {
    const response = await api.post("/api/entries/ticket-payments/create/", data)
    return response.data
  },

  getStatistics: async (): Promise<any> => {
    const response = await api.get("/api/entries/ticket-payments/statistics/")
    return response.data
  },
}

// Ticket Service
export const TicketService = {
  verify: async (ticket_number: string): Promise<any> => {
    const response = await api.post("/api/entries/tickets/verify/", { ticket_number })
    return response.data
  },
}

// Gate Assignment Service
export const GateAssignmentService = {
  getAll: async (page = 1, pageSize = 10, search = "", status = ""): Promise<PaginatedResponse<GateAssignment>> => {
    const response = await api.get("/api/entries/gate-assignments/", {
      params: { page, page_size: pageSize, search, status },
    })
    return response.data
  },

  getById: async (id: number): Promise<GateAssignment> => {
    const response = await api.get(`/api/entries/gate-assignments/${id}/`)
    return response.data
  },

  create: async (data: {
    user: number
    gate: string
    is_active: boolean
  }): Promise<GateAssignment> => {
    const response = await api.post("/api/entries/gate-assignments/create/", data)
    return response.data
  },

  update: async (
    id: number,
    data: {
      user: number
      gate: string
      is_active: boolean
    },
  ): Promise<GateAssignment> => {
    const response = await api.put(`/api/entries/gate-assignments/${id}/update/`, data)
    return response.data
  },

  revoke: async (id: number): Promise<GateAssignment> => {
    const response = await api.post(`/api/entries/gate-assignments/${id}/revoke/`)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/api/entries/gate-assignments/${id}/delete/`)
  },

  getAccountsForAssignment: async (): Promise<AccountPerson[]> => {
    const response = await api.get("/api/entries/gate-assignments/accounts/")
    return response.data
  },

  getGateOptions: async (): Promise<any[]> => {
    const response = await api.get("/api/entries/gate-assignments/gate-options/")
    return response.data
  },
}

// Vehicle Visit Service
export const VehicleVisitService = {
  getAll: async (
    page = 1,
    pageSize = 10,
    search = "",
    status = "",
    dateFrom = "",
    dateTo = "",
  ): Promise<PaginatedResponse<VehicleVisit>> => {
    const response = await api.get("/api/entries/vehicle-visits/", {
      params: { page, page_size: pageSize, search, status, date_from: dateFrom, date_to: dateTo },
    })
    return response.data
  },

  getById: async (id: number): Promise<VehicleVisit> => {
    const response = await api.get(`/api/entries/vehicle-visits/${id}/`)
    return response.data
  },

  recordEntry: async (plate: string): Promise<any> => {
    const response = await api.post("/api/entries/vehicle-visits/entry/", { plate })
    return response.data
  },

  verifyVehicle: async (plate: string): Promise<any> => {
    const response = await api.post("/api/entries/vehicle-visits/verify/", { plate })
    return response.data
  },

  updatePayment: async (data: {
    visit_id: number
    plate: string
    amount: number
    operator: string
    phone_number: string
    tx_reference: string
  }): Promise<any> => {
    const response = await api.post("/api/entries/vehicle-visits/payment/", data)
    return response.data
  },

  getStatistics: async (): Promise<any> => {
    const response = await api.get("/api/entries/vehicle-visits/statistics/")
    return response.data
  },
}
